import { createSlice } from "@reduxjs/toolkit";

import {
  addRegularizeAction,
  addTimeSheetAction,
  deleteTimeSheetAction,
  editTimeSheetAction,
  emailAllUserAttendanceReportAction,
  emailAllUserTimesheetReportAction,
  emailTimesheetClientWiseTimeSheetReportAction,
  emailTimesheetVsSchedulingMonthlyReportAction,
  getAllUserAttendanceReportAction,
  getAllUserPayrollReportAction,
  getAttendanceDetailsAction,
  getClientWiseMonthlyTimeSheetReportAction,
  getClientWiseUserTimeSheetReportAction,
  getMonthWiseTimeSheetReportAction,
  getOpenJobChargedHoursTimesheetAction,
  getSingleAttendanceDetailAction,
  getSingleUserAttendanceReportAction,
  getTimeSheetCalendarReportDataAction,
  getTimeSheetDataAction,
  getTimeSheetReportDataAction,
  getTimeSheetStatus,
  getTimeSheetSummaryReportAction,
  getTimeSheetUnfilledReportAction,
  getTimeSheetUserReportAction,
  getTimeSheetVsSchedulingMonthlyReportAction,
  getTimesheetReportHoursForSpecificJobAction,
  getUserScheduledHoursAction,
} from "@/redux/timeSheet/actions";
import { initialState } from "@/redux/timeSheet/initialState";
import { timeSheetReducer } from "@/redux/timeSheet/reducer";
import type { TimeSheetType } from "@/redux/timeSheet/timeSheetType";

export const timeSheetSlice = createSlice({
  name: "timeSheet",
  initialState,
  reducers: timeSheetReducer,

  extraReducers: (builder) => {
    builder.addCase(getTimeSheetDataAction.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getTimeSheetDataAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timeSheetData: result,
          totalTime: result.total_time,
        };
      }
    );

    builder.addCase(
      getTimeSheetDataAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(addTimeSheetAction.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      addTimeSheetAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      addTimeSheetAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(editTimeSheetAction.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      editTimeSheetAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            statusCode: status_code,
            error: message,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      editTimeSheetAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(deleteTimeSheetAction.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      deleteTimeSheetAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      deleteTimeSheetAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getTimeSheetReportDataAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetReportDataAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const {
          success,
          message,
          result,
          status_code,
          total_count,
          total_time_difference,
          billable_sum,
          non_billable_sum,
        } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timeSheetReportDataList: result,
          timeSheetReportTotalCount: total_count,
          statusCode: status_code,
          success,
          reportTotalTime: total_time_difference,
          reportTotalBillableTime: billable_sum,
          reportTotalNonBillableTime: non_billable_sum,
        };
      }
    );

    builder.addCase(
      getTimeSheetReportDataAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getTimeSheetUserReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetUserReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const {
          success,
          message,
          result,
          status_code,
          total_count,
          total_time_difference = 0,
          billable_sum,
          non_billable_sum,
        } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timeSheetReportDataList: result,
          timeSheetReportTotalCount: total_count,
          statusCode: status_code,
          // success,
          reportTotalTime: total_time_difference,
          reportTotalBillableTime: billable_sum,
          reportTotalNonBillableTime: non_billable_sum,
        };
      }
    );

    builder.addCase(
      getTimeSheetUserReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getTimeSheetUnfilledReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetUnfilledReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, users_with_timesheets, status_code } =
          payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          unfilledTimesheetReportList:
            users_with_timesheets[0]?.unfilled_timesheets,
          unfilledTimesheetCount:
            users_with_timesheets[0]?.unfilled_timesheet_count,
          statusCode: status_code,
          // success,
        };
      }
    );

    builder.addCase(
      getTimeSheetUnfilledReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getTimesheetReportHoursForSpecificJobAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimesheetReportHoursForSpecificJobAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { data, success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          reportForSpecificJobList: data.result,
          reportForSpecificJobTotalCount: data.total_count,
          totalWorkingHoursOnSpecificJob: data.total_work_hours_sum,
        };
      }
    );

    builder.addCase(
      getTimesheetReportHoursForSpecificJobAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      emailAllUserTimesheetReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      emailAllUserTimesheetReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      emailAllUserTimesheetReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getTimeSheetSummaryReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetSummaryReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const {
          success,
          message,
          result,
          status_code,
          count,
          users_without_fill_timesheet,
          users_without_fill_timesheet_count,
          total_sums,
        } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timesheetSummaryReportList: result,
          timsheetSummaryReportTotal: count,
          billableSumTotal: total_sums?.billable_sum_total,
          nonBillableSumTotal: total_sums?.non_billable_sum_total,
          totalHoursSumTotal: total_sums?.total_hours_sum_total,
          employeeWithoutFillTimesheetList: users_without_fill_timesheet,
          employeeWithoutFillTimesheetCount: users_without_fill_timesheet_count,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      getTimeSheetSummaryReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getTimeSheetCalendarReportDataAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetCalendarReportDataAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message ?? null,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timeSheetReportCalenderDataList: result,
          statusCode: status_code,
          // success,
        };
      }
    );

    builder.addCase(
      getTimeSheetCalendarReportDataAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getAllUserAttendanceReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllUserAttendanceReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          allUserAttendanceReportList: result,
          reportListCount: count,
          statusCode: status_code,
          // success,
        };
      }
    );

    builder.addCase(
      getAllUserAttendanceReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getAllUserPayrollReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAllUserPayrollReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          allUserPayrollReportList: result,
          reportListCount: count,
          statusCode: status_code,
          // success,
        };
      }
    );

    builder.addCase(
      getAllUserPayrollReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getSingleUserAttendanceReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getSingleUserAttendanceReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          attendanceReportList: result,
          statusCode: status_code,
          reportListCount: count,
          // success,
        };
      }
    );

    builder.addCase(
      getSingleUserAttendanceReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getSingleAttendanceDetailAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        singleFetching: true,
      })
    );

    builder.addCase(
      getSingleAttendanceDetailAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            singleFetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          singleFetching: false,
          singleAttendanceDetail: result,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      getSingleAttendanceDetailAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        singleFetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      emailAllUserAttendanceReportAction.pending,
      (state: TimeSheetType) => ({
        // fetching: true,
        ...state,
      })
    );

    builder.addCase(
      emailAllUserAttendanceReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      emailAllUserAttendanceReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getTimeSheetVsSchedulingMonthlyReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getTimeSheetVsSchedulingMonthlyReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code, total_count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          timesheetVsSchedulingMonthlyList: result,
          timesheetVsSchedulingMonthlyTotalCount: total_count,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      getTimeSheetVsSchedulingMonthlyReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      emailTimesheetVsSchedulingMonthlyReportAction.pending,
      (state: TimeSheetType) => ({
        // fetching: true,
        ...state,
      })
    );

    builder.addCase(
      emailTimesheetVsSchedulingMonthlyReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      emailTimesheetVsSchedulingMonthlyReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      emailTimesheetClientWiseTimeSheetReportAction.pending,
      (state: TimeSheetType) => ({
        // fetching: true,
        ...state,
      })
    );

    builder.addCase(
      emailTimesheetClientWiseTimeSheetReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            error: message,
            statusCode: status_code,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          message,
        };
      }
    );

    builder.addCase(
      emailTimesheetClientWiseTimeSheetReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getClientWiseUserTimeSheetReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getClientWiseUserTimeSheetReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          clientWiseUserTimeSheetList: result,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      getClientWiseUserTimeSheetReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(addRegularizeAction.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      addRegularizeAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      addRegularizeAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getClientWiseMonthlyTimeSheetReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getClientWiseMonthlyTimeSheetReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          clientWiseMonthlyTimesheetReportList: result?.data,
          clientWiseMonthlyReportCount: result?.count,
          timesheetReportTargetHours: result?.client_details?.target_hours,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      getClientWiseMonthlyTimeSheetReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getMonthWiseTimeSheetReportAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getMonthWiseTimeSheetReportAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          monthWiseTimesheetReportList: result?.data,
          monthWiseTimesheetReportTotalCount: result?.count,
          statusCode: status_code,
          success,
        };
      }
    );

    builder.addCase(
      getMonthWiseTimeSheetReportAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getOpenJobChargedHoursTimesheetAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getOpenJobChargedHoursTimesheetAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, status_code, message, sum_of_total_hours, result } =
          payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            success,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          success,
          openJobChargedHoursTimesheetTotalSum: sum_of_total_hours,
          openJobChargedHoursTimesheetList: result,
        };
      }
    );

    builder.addCase(
      getOpenJobChargedHoursTimesheetAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(getTimeSheetStatus.pending, (state: TimeSheetType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getTimeSheetStatus.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, status_code, message, result } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            error: message,
            success,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          enableBackdatedTimesheetEntry:
            result.enable_backdated_timesheet_entry,
        };
      }
    );

    builder.addCase(
      getTimeSheetStatus.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
        fetching: false,
      })
    );

    builder.addCase(
      getAttendanceDetailsAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAttendanceDetailsAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          regularizationCount: result?.monthly_regularizations,
          statusCode: status_code,
          // success,
        };
      }
    );

    builder.addCase(
      getAttendanceDetailsAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getUserScheduledHoursAction.pending,
      (state: TimeSheetType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUserScheduledHoursAction.fulfilled,
      (state: TimeSheetType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          userScheduledHoursData: result,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      getUserScheduledHoursAction.rejected,
      (state: TimeSheetType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );
  },
});

export const {
  resetTimeSheetSuccess,
  resetTimeSheetError,
  resetTimeSheetMessage,
  resetTimeSheetStatusCode,
  resetTimeSheetTotalTime,
  resetTimeSheetListing,
  resetTimeSheetReportListing,
  resetTimeSheetReportTotalCount,
  resetUserTimeSheetReportSuccess,
  resetUserViewTimeSheetReportStatusCode,
  resetUserViewTimeSheetReportError,
  resetUserViewTimeSheetReportMessage,
  resetTimeSheetReportTotalTime,
  resetUnfilledTimesheetCount,
  resetUnfilledTimesheetReportList,
  resetReportForSpecificJobList,
  resetReportForSpecificJobTotalCount,
  resetTotalWorkingHoursOnSpecificJob,
  resetTimeSheetReportBillableTotalTime,
  resetTimeSheetReportNonBillableTotalTime,
  resetTimeSheetSummaryReportListing,
  resetTimeSheetSummaryReportTotalCount,
  resetBillableSumTotal,
  resetNonBillableSumTotal,
  resetTotalHoursSumTotal,
  resetEmployeeWithoutFillTimesheetList,
  resetEmployeeWithoutFillTimesheetCount,
  resetTimeSheetReportCalenderDataList,
  resetAllUserAttendanceReportList,
  resetSingleAttendaceReportList,
  resetSingleAttendanceDetail,
  resetAttendanceReportListCount,
  resetTimesheetVsSchedulingMonthlyList,
  resetTimesheetVsSchedulingMonthlyTotalCount,
  resetClientWiseUserTimeSheetList,
  resetClientWiseMonthlyReportCount,
  resetClientWiseMonthlyTimesheetReportList,
  resetMonthWiseTimesheetReportTotalCount,
  resetMonthWiseTimesheetReportList,
  resetTimesheetReportTargetHours,
  resetOpenJobChargedHoursTimesheetList,
  resetOpenJobChargedHoursTimesheetTotalSum,
  resetEnableBackdatedTimesheetEntry,
  resetAllUserPayrollReportList,
  resetUserScheduledHoursData,
} = timeSheetSlice.actions;
